import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Utensils, Trophy, ArrowRight, Sparkles } from "lucide-react";

// Sunday, October 11, 2026, 11:00 AM CDT (UTC-5)
const TARGET_MS = new Date("2026-10-11T16:00:00Z").getTime();

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return { days, hours, mins, secs, done: diff === 0 };
  }, [now, target]);
}

function Unit({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <div
        data-testid={`countdown-${label.toLowerCase()}`}
        className="tabular-nums heading-serif text-4xl sm:text-5xl md:text-6xl font-bold text-white drop-shadow-[0_2px_20px_rgba(255,255,255,0.35)] leading-none"
      >
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-white/85">
        {label}
      </div>
    </div>
  );
}

export default function FamilyDayCountdown() {
  const { days, hours, mins, secs, done } = useCountdown(TARGET_MS);

  return (
    <section
      data-testid="home-family-day-countdown"
      className="relative overflow-hidden text-white"
    >
      {/* Vibrant celebration background */}
      <div className="absolute inset-0 bg-gradient-to-br from-npbc-red via-[#a80d26] to-[#7a0a1b]" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_15%_0%,rgba(212,175,55,0.35),transparent_55%),radial-gradient(900px_500px_at_100%_100%,rgba(255,220,120,0.28),transparent_55%),radial-gradient(600px_400px_at_50%_50%,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.09] mix-blend-overlay [background-image:radial-gradient(rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:3px_3px]" />
        {/* Confetti-ish dots */}
        <div className="absolute top-8 left-[8%] h-3 w-3 rounded-full bg-npbc-gold shadow-[0_0_20px_rgba(212,175,55,0.8)]" />
        <div className="absolute top-16 right-[12%] h-2 w-2 rounded-full bg-white/80" />
        <div className="absolute bottom-10 left-[20%] h-2.5 w-2.5 rounded-full bg-npbc-gold" />
        <div className="absolute bottom-16 right-[28%] h-1.5 w-1.5 rounded-full bg-white" />
        <div className="absolute top-1/3 right-[6%] h-2 w-2 rounded-full bg-npbc-gold/90" />
      </div>

      <div className="container-npbc relative py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
          {/* Left — copy & CTA */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-3 py-1.5 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-npbc-gold" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-npbc-gold">
                Special Sunday · Coming Up
              </span>
            </div>
            <h2 className="mt-5 heading-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] drop-shadow-[0_2px_20px_rgba(0,0,0,0.25)]">
              Friends &amp; Family Day
            </h2>
            <p className="mt-4 text-white/90 text-base sm:text-lg max-w-xl leading-relaxed">
              Bring your family. Invite your friends. A day of worship, fellowship, a shared meal, and
              door prizes — the <span className="text-npbc-gold font-bold">largest family group wins $100.</span>
            </p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/90">
              <div className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4 text-npbc-gold" />
                Sunday, October 11 · 11:00 AM
              </div>
              <div className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-npbc-gold" />
                533 S. Mendenhall Rd.
              </div>
              <div className="inline-flex items-center gap-2">
                <Utensils className="h-4 w-4 text-npbc-gold" />
                Fellowship meal after service
              </div>
              <div className="inline-flex items-center gap-2">
                <Trophy className="h-4 w-4 text-npbc-gold" />
                $100 for largest family
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/family-day"
                data-testid="home-countdown-rsvp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-npbc-gold text-npbc-black font-bold px-6 py-3 text-sm shadow-[0_10px_30px_-5px_rgba(212,175,55,0.6)] hover:brightness-105 hover:scale-[1.02] transition-all"
              >
                RSVP for Family Day
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/family-day#rsvp"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/50 text-white font-semibold px-5 py-2.5 text-sm hover:bg-white hover:text-npbc-red transition"
              >
                See details
              </Link>
            </div>
          </motion.div>

          {/* Right — countdown card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="rounded-3xl bg-white/15 border-2 border-npbc-gold/40 backdrop-blur-xl p-6 md:p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-npbc-gold">
                  {done ? "It's Today!" : "Countdown to Family Day"}
                </div>
                <div className="text-[10px] font-mono text-white/70 tabular-nums">
                  {new Date(TARGET_MS).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>

              {done ? (
                <div className="mt-10 mb-6 text-center">
                  <div className="heading-serif text-4xl md:text-5xl text-npbc-gold">
                    Welcome Home!
                  </div>
                  <p className="mt-3 text-white/85 text-sm">
                    Service starts at 11 AM. See you in the sanctuary.
                  </p>
                </div>
              ) : (
                <div className="mt-8 grid grid-cols-4 gap-3 md:gap-6">
                  <Unit label="Days" value={days} />
                  <Unit label="Hours" value={hours} />
                  <Unit label="Mins" value={mins} />
                  <Unit label="Secs" value={secs} />
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-npbc-gold animate-pulse" />
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/80">
                    A seat is waiting for you
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
